import { InputBaseComponentProps } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Fragment, forwardRef, useRef, useState } from "react";
import { Chips } from "./Chips";
import { Dialog } from "./EffectDialog";
import { DataType } from "./EffectSearch";
import { generateChildren, mapData, parseValue } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    chips: {
      background: theme.primary500,
      color: theme.white50,
      borderRadius: "50px",
      padding: theme.spacing(2, 8),
      margin: theme.spacing(2, 0),
      fontSize: "14px",
      display: "inline-block",
      userSelect: "none",
    },
    input: {
      minWidth: "100%",
      height: "unset",
      gap: theme.spacing(2.5),
      display: "inline-block",
      flexWrap: "wrap",
      alignItems: "center",
      zIndex: 0,
      position: "relative",
    },
    placeholder: {
      color: "#b5b5b5",
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 0,
    },
    wrapper: {
      position: "relative",
      width: "calc(100% - 48px)",
    },
  }),
);

interface Props extends InputBaseComponentProps {
  items: Array<DataType>;
  setFieldValue: (value: string[]) => void;
  setDialog: (dialog: Dialog | null) => void;
  setOpen: (val: boolean) => void;
}

export const EffectInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const [isEditingChips, setIsEdittingChips] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const classes = useStyles();
  const mappedValue = generateChildren(props.value || "", props.items);
  const isAnyValue = mappedValue.length > 0;
  const valueRef = useRef<HTMLSpanElement>(null);

  const saveValue = () => {
    return props.setFieldValue(
      parseValue(valueRef.current?.innerHTML || "", props.items),
    );
  };

  const onPaste = (event: React.ClipboardEvent<HTMLSpanElement>) => {
    event.preventDefault();
    const clipboardTextOnly = event.clipboardData.getData("text");
    const selection = window.getSelection();

    if (!selection || !selection.rangeCount) return;

    // Get the currently selected range
    const range = selection.getRangeAt(0);
    range.deleteContents();

    // Create a text node with the formatted text and insert it at the current range
    const textNode = document.createTextNode(clipboardTextOnly);
    range.insertNode(textNode);

    range.setStartAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const moveCursorToStartIfEmpty = () => {
    if (!valueRef.current) return;
    // FIREFOX FIX for wrongly handling contentEditable

    const selection = window.getSelection();
    const range = document.createRange();

    // Check if the span is empty
    if (valueRef.current.innerText?.trim() === "") {
      range.setStart(valueRef.current, 0);
      range.collapse(true); // Move cursor to the beginning
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  return (
    <div className={classes.wrapper} ref={ref}>
      {!isFocused && !isAnyValue && (
        <span className={classes.placeholder}>{props.placeholder}</span>
      )}
      <span
        ref={valueRef}
        data-test-id="effect-search-input"
        className={`${props.className} ${classes.input}`}
        contentEditable={isEditingChips ? false : true}
        suppressContentEditableWarning={true}
        onBlur={(e) => {
          if (isEditingChips) return;
          props.setFieldValue(parseValue(e.target.innerHTML, props.items));
          setIsFocused(false);
        }}
        onClick={() => {
          setIsFocused(true);
          moveCursorToStartIfEmpty();
        }}
        onPaste={onPaste}
      >
        {mappedValue.map(({ type, text, code }, index) => {
          if (type === "chips") {
            return (
              <Fragment key={code + index}>
                &nbsp;
                <Chips
                  key={index}
                  code={code}
                  items={mapData(props.items)}
                  setIsEdittingChips={setIsEdittingChips}
                  saveValue={saveValue}
                  valueRef={valueRef.current}
                  setOpen={props.setOpen}
                />
                &nbsp;
              </Fragment>
            );
          }

          return ` ${text} `;
        })}
      </span>
    </div>
  );
});
